/**
 * External Auth API Service
 *
 * API calls for external user authentication (portal users).
 * Uses dedicated externalAxiosInstance for authenticated calls to ensure
 * the external token is used (not the internal user token).
 */
import type { PortalInfo } from '@/generated/api/models/portalInfo';

import axiosInstance from '../../shared/api/axiosInstance';
import externalAxiosInstance from './externalAxiosInstance';

export interface ExternalLoginRequest {
  email: string;
  password: string;
  tenant_slug: string;
}

export interface ExternalLoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: ExternalUser;
}

export interface ExternalRegisterRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  tenant_slug: string;
}

export interface ExternalUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  status: string;
  user_type: string;
  contact_id: string | null;
  tenant_id: string;
  external_role: string | null;
  created_at: string;
}

/**
 * Public portal descriptor served by `GET /api/v1/external/portal/info/{slug}`.
 *
 * Re-exported from the orval-generated OpenAPI models so it can never drift from
 * the backend contract (`shared/schemas/user_management/portal_info.schema.json`)
 * — the portal nav and `PortalFeatureRoute` gate on these flags, so a hand-typed
 * copy going stale silently mis-gates the whole portal.
 */
export type { PortalInfo };

export interface ExternalSSOInitResponse {
  auth_url: string;
  state: string;
}

export interface RegistrationCheckResponse {
  can_register: boolean;
  reason?: string;
  contact_found?: boolean;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  new_password: string;
}

export interface VerifyResetTokenResponse {
  valid: boolean;
  email?: string;
}

class ExternalAuthApi {
  /**
   * Get public portal info by tenant slug
   */
  async getPortalInfo(tenantSlug: string): Promise<PortalInfo> {
    const response = await axiosInstance.get(`/api/v1/external/portal/info/${tenantSlug}`);
    return response.data.data;
  }

  /**
   * Check if email can register for a tenant
   */
  async checkRegistration(tenantSlug: string, email: string): Promise<RegistrationCheckResponse> {
    const response = await axiosInstance.get(
      `/api/v1/external/portal/check-registration?tenant_slug=${tenantSlug}&email=${encodeURIComponent(email)}`
    );
    return response.data.data;
  }

  /**
   * Login as external user
   */
  async login(data: ExternalLoginRequest): Promise<ExternalLoginResponse> {
    const response = await axiosInstance.post('/api/v1/external/auth/login', data);
    return response.data.data;
  }

  /**
   * Register as external user
   */
  async register(data: ExternalRegisterRequest): Promise<{ message_code?: string; user_id?: string; status: string; email_sent?: boolean }> {
    const response = await axiosInstance.post('/api/v1/external/auth/register', data);
    return response.data.data;
  }

  /**
   * Start an external-portal SSO flow.
   * Returns the provider auth URL + a state token (stored for CSRF verification).
   */
  async ssoInit(provider: string, tenantSlug: string, redirectUri: string): Promise<ExternalSSOInitResponse> {
    const response = await axiosInstance.post(`/api/v1/external/auth/sso/${provider}/init`, {
      tenant_slug: tenantSlug,
      redirect_uri: redirectUri,
    });
    return response.data.data;
  }

  /**
   * Complete an external-portal SSO flow (exchange the OAuth code for an
   * EXTERNAL session, tenant-scoped to the portal).
   */
  async ssoCallback(
    provider: string,
    tenantSlug: string,
    code: string,
    redirectUri: string,
  ): Promise<ExternalLoginResponse> {
    const response = await axiosInstance.post(`/api/v1/external/auth/sso/${provider}/callback`, {
      tenant_slug: tenantSlug,
      code,
      redirect_uri: redirectUri,
    });
    return response.data.data;
  }

  /**
   * Verify email address
   */
  async verifyEmail(token: string): Promise<{ message_code: string; status: string; email?: string }> {
    const response = await axiosInstance.post('/api/v1/external/auth/verify-email', { token });
    return response.data.data;
  }

  /**
   * Resend verification email
   * Uses the shared /api/v1/auth/resend-verification endpoint which handles both internal and external users
   */
  async resendVerification(email: string): Promise<{ message_code: string }> {
    const response = await axiosInstance.post('/api/v1/auth/resend-verification', { email });
    return response.data.data;
  }

  /**
   * Get current external user profile
   * Uses dedicated external axios instance to ensure external token is used
   */
  async getMe(): Promise<ExternalUser> {
    const response = await externalAxiosInstance.get('/api/v1/external/auth/me');
    return response.data.data;
  }

  /**
   * Update current external user profile
   * Uses dedicated external axios instance to ensure external token is used
   */
  async updateMe(data: { first_name?: string; last_name?: string; password?: string }): Promise<ExternalUser> {
    const response = await externalAxiosInstance.put('/api/v1/external/auth/me', data);
    return response.data.data;
  }

  /**
   * Request password reset email
   * Uses the shared /api/v1/auth/forgot-password endpoint which handles both internal and external users
   */
  async forgotPassword(data: ForgotPasswordRequest): Promise<{ message_code: string }> {
    const response = await axiosInstance.post('/api/v1/auth/forgot-password', data);
    return response.data.data;
  }

  /**
   * Verify password reset token
   * Uses the shared /api/v1/auth/verify-reset-token endpoint
   */
  async verifyResetToken(token: string): Promise<VerifyResetTokenResponse> {
    const response = await axiosInstance.post('/api/v1/auth/verify-reset-token', { token });
    return response.data.data;
  }

  /**
   * Reset password using token from email
   * Uses the shared /api/v1/auth/reset-password endpoint
   */
  async resetPassword(data: ResetPasswordRequest): Promise<{ message_code: string }> {
    const response = await axiosInstance.post('/api/v1/auth/reset-password', data);
    return response.data.data;
  }
}

export const externalAuthApi = new ExternalAuthApi();
